import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { fetchAdmin, toErrorMessage } from '../../../admin/api';
import { fDateTime } from '../../../utils/formatTime';
import {
  AdminDataTable,
  AdminDetailPanel,
  AdminNoDataState,
  AdminStatusChip,
  AdminTimeline,
  DataInspector,
} from '../shared';

const DRAWER_WIDTH = 560;

function LoadingSection() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={220} />
      <Skeleton variant="rounded" height={260} />
    </Stack>
  );
}

function formatText(value) {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return `${value}`;
}

function buildTimelineItems(items) {
  return items.map((row) => ({
    id: `${row.id}`,
    title: row.decision_code || 'Decision event',
    description: `${formatText(row.decision_scope)} · allowed=${formatText(row.allowed)} · requires_review=${formatText(row.requires_review)}`,
    time: fDateTime(row.created_at) || 'N/A',
    color: row.requires_review ? 'warning' : row.allowed ? 'success' : 'error',
  }));
}

function isSuccessStatus(status) {
  if (typeof status !== 'string') {
    return false;
  }

  const normalizedStatus = status.trim().toLowerCase();
  return normalizedStatus === 'success' || normalizedStatus === 'ok' || normalizedStatus === 'resolved';
}

export default function TransactionSideDrawer({ open, onClose, reference }) {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState({
    loading: false,
    error: null,
    detail: null,
    timeline: [],
  });
  const [actionState, setActionState] = useState({
    queryingProvider: false,
    retryingVerify: false,
  });
  const [inspectorState, setInspectorState] = useState({
    open: false,
    title: 'Data Inspector',
    subtitle: 'Inspect raw backend payloads returned for this transaction.',
    data: undefined,
  });

  useEffect(() => {
    let active = true;

    if (!open || !reference) {
      return () => {
        active = false;
      };
    }

    setState({ loading: true, error: null, detail: null, timeline: [] });

    const load = async () => {
      try {
        const [detailResponse, timelineResponse] = await Promise.all([
          fetchAdmin(`/transactions/${reference}`),
          fetchAdmin(`/transactions/timeline/${reference}`),
        ]);

        if (!active) {
          return;
        }

        setState({
          loading: false,
          error: null,
          detail: detailResponse.data || null,
          timeline: timelineResponse.timeline || [],
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setState({
          loading: false,
          error: toErrorMessage(error),
          detail: null,
          timeline: [],
        });
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [open, reference]);

  useEffect(() => {
    if (open) {
      return undefined;
    }

    setInspectorState({
      open: false,
      title: 'Data Inspector',
      subtitle: 'Inspect raw backend payloads returned for this transaction.',
      data: undefined,
    });

    return undefined;
  }, [open]);

  useEffect(() => {
    if (open) {
      return undefined;
    }

    setActionState({
      queryingProvider: false,
      retryingVerify: false,
    });

    return undefined;
  }, [open]);

  const openInspector = (title, data, subtitle) => {
    setInspectorState({
      open: true,
      title,
      subtitle: subtitle || 'Inspect raw backend payloads returned for this transaction.',
      data,
    });
  };

  const handleQueryProvider = async () => {
    if (!reference || actionState.queryingProvider) {
      return;
    }

    setActionState((prev) => ({
      ...prev,
      queryingProvider: true,
    }));

    try {
      const response = await fetchAdmin(`/transactions/payout/query-provider/${reference}`);
      openInspector(
        'Provider Query Response',
        response,
        'Inspect the raw provider query response returned for this payout transaction.'
      );
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setActionState((prev) => ({
        ...prev,
        queryingProvider: false,
      }));
    }
  };

  const handleRetryVerify = async () => {
    if (!reference || actionState.retryingVerify) {
      return;
    }

    setActionState((prev) => ({
      ...prev,
      retryingVerify: true,
    }));

    try {
      const response = await fetchAdmin(`/transactions/payout/verify/${reference}`);
      const variant = isSuccessStatus(response?.status) ? 'success' : 'error';
      enqueueSnackbar(response?.message || 'Verification request completed.', { variant });
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setActionState((prev) => ({
        ...prev,
        retryingVerify: false,
      }));
    }
  };

  const summaryItems = useMemo(() => {
    const transaction = state.detail?.transaction;
    const bankTransfer = state.detail?.linked_data?.bank_transfer;

    return [
      { label: 'Reference', value: formatText(transaction?.reference || reference) },
      { label: 'Transaction ID', value: formatText(transaction?.id) },
      { label: 'Type', value: formatText(transaction?.transaction_type) },
      { label: 'Status', value: formatText(transaction?.status) },
      { label: 'Linked bank', value: formatText(bankTransfer?.bank_name) },
    ];
  }, [reference, state.detail]);

  const bankTransferItems = useMemo(() => {
    const bankTransfer = state.detail?.linked_data?.bank_transfer;

    if (!bankTransfer) {
      return [];
    }

    return [
      { label: 'Account name', value: formatText(bankTransfer.account_name) },
      { label: 'Account number', value: formatText(bankTransfer.account_number) },
      { label: 'Bank name', value: formatText(bankTransfer.bank_name) },
      { label: 'Reference', value: formatText(bankTransfer.reference) },
      { label: 'Status', value: formatText(bankTransfer.status) },
    ];
  }, [state.detail]);

  const ledgerRows = useMemo(
    () =>
      (state.detail?.ledger_entries || []).map((row) => ({
        id: row.id,
        walletType: formatText(row.wallet_type),
        entryType: formatText(row.entry_type),
        amount: formatText(row.amount),
        balanceBefore: formatText(row.balance_before),
        balanceAfter: formatText(row.balance_after),
        createdAt: fDateTime(row.created_at) || 'N/A',
      })),
    [state.detail]
  );

  const walletSnapshotRows = useMemo(
    () =>
      (state.detail?.wallet_snapshots || []).map((row, index) => ({
        id: row.id || `${index}-${row.wallet_type || 'snapshot'}`,
        walletType: formatText(row.wallet_type),
        balance: formatText(row.balance),
        availableBalance: formatText(row.available_balance),
        createdAt: fDateTime(row.created_at) || 'N/A',
      })),
    [state.detail]
  );

  const retryRows = useMemo(
    () =>
      (state.detail?.retries || []).map((row, index) => ({
        id: row.id || `${index}-${row.status || 'retry'}`,
        status: formatText(row.status),
        provider: formatText(row.provider),
        message: formatText(row.message),
        createdAt: fDateTime(row.created_at) || 'N/A',
      })),
    [state.detail]
  );

  const transferAttempts = state.detail?.transfer_attempts || [];
  const timelineItems = useMemo(() => buildTimelineItems(state.timeline || []), [state.timeline]);
  const transactionStatus = state.detail?.transaction?.status;
  const bankTransfer = state.detail?.linked_data?.bank_transfer || null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: DRAWER_WIDTH },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={1}>
              <Typography variant="h6">Transaction Details</Typography>
              <Typography variant="body2" color="text.secondary">
                Probe transaction state, linked domain data, and policy decisions for the selected reference.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Reference: {reference || 'N/A'}
                </Typography>
                {transactionStatus ? <AdminStatusChip status={transactionStatus} /> : null}
              </Stack>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close transaction drawer">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
            {state.loading ? <LoadingSection /> : null}

            {!state.loading && state.error ? (
              <Alert severity="error" variant="outlined">
                {state.error}
              </Alert>
            ) : null}

            {!state.loading && !state.error && !state.detail ? (
              <AdminNoDataState
                title="No transaction loaded"
                description="Open the drawer from a transaction row to load its detail payload and policy timeline."
              />
            ) : null}

            {!state.loading && !state.error && state.detail ? (
              <Stack spacing={3}>
                <AdminDetailPanel title="Transaction Summary" items={summaryItems} />

                {bankTransferItems.length ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle1">Linked Bank Transfer</Typography>
                            {/* <Typography variant="body2" color="text.secondary">
                              Inspect the linked transfer record returned with this transaction detail payload.
                            </Typography> */}
                          </Stack>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              openInspector(
                                'Linked Bank Transfer',
                                bankTransfer,
                                'Inspect the raw linked bank transfer payload returned for this transaction.'
                              )
                            }
                          >
                            Inspect
                          </Button>
                        </Stack>
                        <Divider />
                        <Stack spacing={1.5}>
                          {bankTransferItems.map((item) => (
                            <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
                              <Typography variant="body2" color="text.secondary">
                                {item.label}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} textAlign="right">
                                {item.value}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                        <Stack spacing={1.5}>
                          <Divider />
                          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            <LoadingButton
                              variant="outlined"
                              color="secondary"
                              loading={actionState.queryingProvider}
                              onClick={handleQueryProvider}
                              disabled={!reference}
                              sx={{ minWidth: 180 }}
                            >
                            Query provider status
                            </LoadingButton>
                            <LoadingButton
                              variant="outlined"
                              color="secondary"
                              loading={actionState.retryingVerify}
                              onClick={handleRetryVerify}
                              disabled={!reference}
                              sx={{ minWidth: 140 }}
                            >
                            Re-verify
                            </LoadingButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                {transferAttempts.length ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={2.5}>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle1">Bank Transfer Attempts</Typography>
                          {/* <Typography variant="body2" color="text.secondary">
                            Review each transfer attempt and inspect the raw request and response payloads returned by the backend.
                          </Typography> */}
                        </Stack>
                        <Divider />
                        <Stack spacing={2}>
                          {transferAttempts.map((attempt, index) => (
                            <Card
                              key={attempt.id || `${attempt.transfer_id || 'transfer'}-${index}`}
                              variant="outlined"
                              sx={{ bgcolor: 'background.default' }}
                            >
                              <CardContent>
                                <Stack spacing={2}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                    <Stack spacing={0.5}>
                                      <Typography variant="subtitle2">
                                        Attempt {formatText(attempt.attempt_no || index + 1)}
                                      </Typography>
                                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        {attempt.status ? <AdminStatusChip status={attempt.status} /> : null}
                                        <Typography variant="caption" color="text.secondary">
                                          Channel: {formatText(attempt.channel_code)}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        openInspector(
                                          `Transfer Attempt ${formatText(attempt.attempt_no || index + 1)}`,
                                          attempt,
                                          'Inspect the transfer attempt record.'
                                        )
                                      }
                                    >
                                      Inspect
                                    </Button>
                                  </Stack>

                                  <Stack spacing={1.25}>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                      <Typography variant="body2" color="text.secondary">
                                        Attempt ID
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {formatText(attempt.id)}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                      <Typography variant="body2" color="text.secondary">
                                        Error message
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {formatText(attempt.error_message)}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                      <Typography variant="body2" color="text.secondary">
                                        External ID
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {formatText(attempt.external_id)}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                                      <Typography variant="body2" color="text.secondary">
                                        Created
                                      </Typography>
                                      <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {fDateTime(attempt.created_at) || 'N/A'}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                <AdminDataTable
                  title="Ledger Entries"
                  description="Ledger rows linked to the selected transaction."
                  columns={[
                    { field: 'walletType', headerName: 'Wallet Type' },
                    { field: 'entryType', headerName: 'Entry Type' },
                    { field: 'amount', headerName: 'Amount' },
                    { field: 'balanceBefore', headerName: 'Before' },
                    { field: 'balanceAfter', headerName: 'After' },
                    { field: 'createdAt', headerName: 'Created' },
                  ]}
                  rows={ledgerRows}
                  emptyTitle="No ledger entries"
                  emptyDescription="No ledger rows were returned for the selected transaction."
                />

                <AdminDataTable
                  title="Wallet Snapshots"
                  description="Snapshot data returned alongside transaction detail."
                  columns={[
                    { field: 'walletType', headerName: 'Wallet Type' },
                    { field: 'balance', headerName: 'Balance' },
                    { field: 'availableBalance', headerName: 'Available' },
                    { field: 'createdAt', headerName: 'Created' },
                  ]}
                  rows={walletSnapshotRows}
                  emptyTitle="No wallet snapshots"
                  emptyDescription="The transaction detail payload did not include wallet snapshots."
                />

                <AdminDataTable
                  title="Retries"
                  description="Retry attempts returned for the selected transaction."
                  columns={[
                    { field: 'status', headerName: 'Status' },
                    { field: 'provider', headerName: 'Provider' },
                    { field: 'message', headerName: 'Message' },
                    { field: 'createdAt', headerName: 'Created' },
                  ]}
                  rows={retryRows}
                  emptyTitle="No retries"
                  emptyDescription="No retry records were returned for the selected transaction."
                />

                <AdminTimeline title="Policy Timeline" items={timelineItems} />
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Drawer>

      <DataInspector
        open={inspectorState.open}
        onClose={() => setInspectorState((prev) => ({ ...prev, open: false }))}
        title={inspectorState.title}
        subtitle={inspectorState.subtitle}
        data={inspectorState.data}
      />
    </>
  );
}

TransactionSideDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  reference: PropTypes.string,
};

TransactionSideDrawer.defaultProps = {
  reference: '',
};
